import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { NotificationSeverity } from '@cd3p/core/modules/notifications';
import {
  APIProvider,
  APIProviderSettings,
  APIUser,
} from '@cd3p/core/types/api';
import { getServerErrorTitle } from '@cd3p/core/utils/common';
import { OpenInNewOutlined } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  ConfirmationDialog,
  Stack,
  Tab,
  Tabs,
  TabsBottomLine,
  Typography,
} from 'components';

import {
  InitiateAccountFormT,
  InitiateAccountPopup,
} from './InitiateAccountPopup';

import {
  Navigate,
  useMatch,
  useNavigate,
  useParams,
  useSelector,
  useTranslation,
} from 'third-party';

import {
  COMPANY_SETTINGS_PATH,
  INTEGRATIONS_PATH,
  ORDER_FIELDS_SCHEMA_PATH,
  contractorsUrl,
  customerAccountIntegrationsUrl,
  customerAccountOrderFieldsSchemaUrl,
  customerAccountSettingsUrl,
  openInstanceUrl,
} from 'constants/url';

import { useAdminCustomerAccountsList } from 'modules/adminCustomerAccountsList';
import { useNotifications } from 'modules/notifications';

import { adminCustomerAccountsList } from 'selectors';

import { CompanySettingsTab } from 'features/PlantSettings/CompanySettingsTab/CompanySettingsTab';
import { IntegrationSettingsTab } from 'features/PlantSettings/IntegrationSettingsTab/IntegrationSettingsTab';
import { OrderFieldsSchemaTab } from 'features/PlantSettings/OrderCustomFieldsTab/OrderFieldsSchemaTab';

import { TabTitle, TabWrapper, TabsWrapper } from 'styles/theme';

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 1rem 1.2rem 1.5rem;
  flex-direction: row;
`;

const StyledTab = styled(Tab)`
  flex-grow: initial;
  padding: 0 5rem;
`;

const Title = styled(Typography)`
  font-size: 2rem;
  font-weight: 900;
  text-transform: uppercase;
  color: ${props => props.theme.custom.palette.secondary900};
`;

const LoadingContainer = styled(Stack)`
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledOpenInNewIcon = styled(OpenInNewOutlined)`
  font-size: 1.6rem;
  padding-right: 0.8rem;
`;

const StyledButton = styled(Button)`
  font-size: 1.2rem;
  font-weight: 700;
`;

enum CustomerAccountDetailsTabs {
  CompanySettings,
  Integrations,
  OrderFieldsSchema,
}

export const CustomerAccountDetails = () => {
  const { contractorId, tabId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const [showInitiateAccountPopup, setShowInitiateAccountPopup] =
    useState(false);
  const [customerAccountAdminEmail, setCustomerAccountAdminEmail] = useState<
    null | string | undefined
  >(undefined);
  const [isProviderSettingsLoading, setIsProviderSettingsLoading] =
    useState(true);

  const { updateCustomerAccount } = useAdminCustomerAccountsList();

  const customerAccountsListItems = useSelector(
    adminCustomerAccountsList.customerAccountsListItems,
  );

  const updateCustomerAccountPending = useSelector(
    adminCustomerAccountsList.updateCustomerAccountPending,
  );

  const selectedCustomerAccount = useMemo(
    () => customerAccountsListItems.find(item => item.id === contractorId),
    [contractorId, customerAccountsListItems],
  );

  const [customerDetails, setCustomerDetails] = useState<null | APIProvider>(
    null,
  );
  const [showDeactivateConfirmation, setShowDeactivateConfirmation] =
    useState(false);

  const matchIndexUrl = useMatch(contractorsUrl(contractorId));
  const currentTab = useMemo(() => {
    switch (tabId) {
      case COMPANY_SETTINGS_PATH:
        return CustomerAccountDetailsTabs.CompanySettings;
      case INTEGRATIONS_PATH:
        return CustomerAccountDetailsTabs.Integrations;
      case ORDER_FIELDS_SCHEMA_PATH:
        return CustomerAccountDetailsTabs.OrderFieldsSchema;
      default:
        return null;
    }
  }, [tabId]);

  const tabs = useMemo(
    () => [
      {
        title: t('settings.companySettingsTab'),
        onClick: () =>
          navigate(customerAccountSettingsUrl(contractorId), {
            replace: true,
          }),
      },
      {
        title: t('settings.integrationsTab'),
        onClick: () =>
          navigate(customerAccountIntegrationsUrl(contractorId), {
            replace: true,
          }),
      },
      {
        title: t('settings.orderCustomFieldsTab'),
        onClick: () =>
          navigate(customerAccountOrderFieldsSchemaUrl(contractorId), {
            replace: true,
          }),
      },
    ],
    [t, navigate, contractorId],
  );

  const onOpenInstanceClick = useCallback(() => {
    if (contractorId) {
      window.open(
        openInstanceUrl(contractorId),
        '_blank',
        'noopener,noreferrer',
      );
    }
  }, [contractorId]);

  const onCustomerSettingsLoaded = useCallback(
    (settings: APIProviderSettings[]) => {
      setIsProviderSettingsLoading(false);
      const providerAdminEmail = settings.find(
        item => item?.key === 'ProviderAdminEmail',
      );
      setCustomerAccountAdminEmail(providerAdminEmail?.value || null);
    },
    [],
  );

  const handleInitiateAccountClick = useCallback(() => {
    setShowInitiateAccountPopup(true);
  }, []);

  const handleInitiateAccountSuccess = useCallback(
    (user: APIUser, formValues: InitiateAccountFormT) => {
      if (contractorId) {
        setCustomerAccountAdminEmail(formValues.email?.value);
      }
    },
    [contractorId],
  );

  const onToggleDeactivate = useCallback(
    async (isDeactivated: boolean) => {
      if (contractorId) {
        const result = await updateCustomerAccount(contractorId!, {
          ...selectedCustomerAccount,
          providerName: selectedCustomerAccount?.providerName || '',
          isDeactivated: isDeactivated,
        });
        if (!result || result.error) {
          addNotification({
            severity: NotificationSeverity.Error,
            message:
              ((result.payload.status === 400 ||
                result.payload.status === 405) &&
                getServerErrorTitle(result)) ||
              t('common.generalError'),
          });
        } else {
          const message = isDeactivated
            ? t('customers.support.deactivateSuccess')
            : t('customers.support.reactivateSuccess');
          addNotification({
            message: message,
          });
          setShowDeactivateConfirmation(false);
        }
      }
    },
    [
      contractorId,
      selectedCustomerAccount,
      addNotification,
      t,
      updateCustomerAccount,
    ],
  );

  const onContractorLoaded = useCallback((data: APIProvider) => {
    setCustomerDetails(data);
  }, []);

  useEffect(() => {
    if (contractorId) {
      setIsProviderSettingsLoading(true);
    }
  }, [contractorId]);

  if (!contractorId) {
    return (
      <LoadingContainer>
        <CircularProgress sx={{ margin: '5rem' }} />
      </LoadingContainer>
    );
  }

  return (
    <>
      <ConfirmationDialog
        onClose={() => {
          setShowDeactivateConfirmation(false);
        }}
        open={showDeactivateConfirmation}
        handleActionClick={() => onToggleDeactivate(true)}
        title={t('common.areYouSure')}
        description={t('customers.support.deactivateActionDescription')}
        actionButtonTitle={t('customers.details.deactivateActionConfirmation')}
        cancelButtonTitle={t('customers.details.deactivateActionCancel')}
        actionPending={updateCustomerAccountPending}
      />
      <HeaderWrapper>
        {showInitiateAccountPopup && (
          <InitiateAccountPopup
            onClosePopup={() => setShowInitiateAccountPopup(false)}
            onSuccess={handleInitiateAccountSuccess}
            customerId={contractorId}
          />
        )}
        <Stack direction="row" alignItems="center">
          <Title variant="h5">
            {(selectedCustomerAccount || customerDetails)?.providerName}
          </Title>
        </Stack>
        <Stack direction="row" gap="1rem">
          <StyledButton
            size="small"
            variant="outlined"
            color="secondary"
            onClick={onOpenInstanceClick}
          >
            <StyledOpenInNewIcon fontSize="small" />
            {t('customers.support.openInstance')}
          </StyledButton>
          <StyledButton
            variant="contained"
            color="primary"
            disabled={isProviderSettingsLoading || !!customerAccountAdminEmail}
            onClick={handleInitiateAccountClick}
          >
            {t('customers.support.initiateAccount')}
          </StyledButton>
          {selectedCustomerAccount?.isDeactivated ? (
            <StyledButton
              variant="contained"
              color="primary"
              disabled={isProviderSettingsLoading}
              onClick={() => onToggleDeactivate(false)}
            >
              {t('customers.support.reactivateAccount')}
            </StyledButton>
          ) : (
            <StyledButton
              variant="outlined"
              color="secondary"
              disabled={isProviderSettingsLoading}
              onClick={() => setShowDeactivateConfirmation(true)}
            >
              {t('customers.support.deactivateAccount')}
            </StyledButton>
          )}
        </Stack>
      </HeaderWrapper>
      <TabsWrapper>
        <Tabs value={currentTab}>
          {tabs.map(tab => {
            return (
              <StyledTab
                key={tab.title}
                label={
                  <TabWrapper>
                    <TabTitle>{tab.title}</TabTitle>
                  </TabWrapper>
                }
                onClick={tab.onClick}
              />
            );
          })}
          <TabsBottomLine />
        </Tabs>
        {contractorId &&
          currentTab === CustomerAccountDetailsTabs.CompanySettings && (
            <CompanySettingsTab
              key={contractorId}
              adminEditRights
              contractorId={contractorId}
              onContractorLoaded={onContractorLoaded}
              onProviderSettingsLoaded={onCustomerSettingsLoaded}
            />
          )}
        {contractorId &&
          currentTab === CustomerAccountDetailsTabs.Integrations && (
            <IntegrationSettingsTab
              key={contractorId}
              contractorId={contractorId}
            />
          )}
        {contractorId &&
          currentTab === CustomerAccountDetailsTabs.OrderFieldsSchema && (
            <OrderFieldsSchemaTab
              key={contractorId}
              providerId={contractorId}
            />
          )}
        {matchIndexUrl && (
          <Navigate to={customerAccountSettingsUrl(contractorId)} replace />
        )}
      </TabsWrapper>
    </>
  );
};
